import { type ReactNode, useEffect, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useAddSubscriberToTenantMutation } from '@features/tenants/api/tenantsApi';
import { Navigate, useLocation, useNavigate } from 'react-router';

import { PageLoader } from '@/components/common';
import { logger } from '@/utils/logger';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Route guard that:
 * 1. Redirects unauthenticated users to login (preserving returnTo)
 * 2. Handles invite params (?invitationKey / ?subscriber) for authenticated users
 *    - invitationKey: forces re-auth through Auth0 so the Rule/Action processes it
 *    - subscriber: calls addnewsubscriberexistingusertotenant API directly
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const [addSubscriber] = useAddSubscriberToTenantMutation();
  const [processingInvite, setProcessingInvite] = useState(false);
  const inviteProcessedRef = useRef(false);

  const searchParams = new URLSearchParams(location.search);
  const invitationKey = searchParams.get('invitationKey');
  const subscriberKey = searchParams.get('subscriber');
  const accessLevel = searchParams.get('access');
  const hasInviteParam = !!(invitationKey || subscriberKey || accessLevel);

  useEffect(() => {
    if (isLoading || !isAuthenticated || inviteProcessedRef.current) return;

    if (invitationKey || accessLevel) {
      inviteProcessedRef.current = true;
      setProcessingInvite(true);
      const authParams: Record<string, string> = {
        signup: 'true',
        screen_hint: 'signup',
      };
      if (invitationKey) authParams.invitationkey = invitationKey;
      if (accessLevel) authParams.access = accessLevel;
      loginWithRedirect({
        authorizationParams: authParams,
        appState: { returnTo: btoa('/') },
      });
    } else if (subscriberKey) {
      inviteProcessedRef.current = true;
      setProcessingInvite(true);
      const cleanUrl = () => {
        const params = new URLSearchParams(location.search);
        params.delete('subscriber');
        const qs = params.toString();
        return location.pathname + (qs ? `?${qs}` : '');
      };
      addSubscriber(subscriberKey)
        .unwrap()
        .then(() => {
          navigate(cleanUrl(), { replace: true });
          setProcessingInvite(false);
        })
        .catch((err) => {
          logger.error('ProtectedRoute: addSubscriberToTenant failed:', err);
          navigate(cleanUrl(), { replace: true });
          setProcessingInvite(false);
        });
    }
  }, [
    isLoading,
    isAuthenticated,
    invitationKey,
    subscriberKey,
    accessLevel,
    loginWithRedirect,
    addSubscriber,
    navigate,
    location.pathname,
    location.search,
  ]);

  if (isLoading) {
    return <PageLoader variant="viewport" />;
  }

  if (!isAuthenticated) {
    const fullPath = location.pathname + location.search;
    const returnTo = btoa(fullPath);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  if (processingInvite || hasInviteParam) {
    return <PageLoader variant="viewport" message="Processing invitation..." />;
  }

  return children;
};
